/**
 * Press section — logo navigation synced to quote panels.
 *
 * Each private theme block renders logo + quote via `{% content_for 'blocks' %}`.
 * Logos are relocated into the Splide track; quote panels stay in the items
 * host — one visible at a time.
 *
 * The active logo is always centred in the row, and the row rotates round it:
 * the carousel is force-mounted as a centre-focused loop at every width, so
 * Splide holds the pressed logo on the centre line and fills both sides with
 * clones. Balancing the row by hand instead — flex `order` plus a measured
 * offset — kept the group centred only while every mark was the same width, and
 * walked the row off to one side once a wide one (Independent) came round.
 *
 * Blocks with a logo but no quote are still press coverage, and with auto change
 * on there is nothing for the logos to stay in step with — so that combination
 * runs as a ticker instead: the row scrolls continuously past cloned logos and
 * the shared carousel stays out of it. Any quote at all keeps the pager.
 *
 * A quote with no logo renders no row control. Auto-change, keyboard, and the
 * dot pager still walk every press item. Among logos, that quote hides the row
 * until the next logo is current, so the previous publication is not left
 * centred under it. The dots stay visible so the quote can be chosen.
 */
(() => {
  const TAG = 'press-carousel';

  // Every ticker copy is duplicated DOM, so the loop needs a ceiling — but on
  // total slides, not sets: a set of one narrow logo needs far more copies to
  // cover the track than a set of six wide ones.
  const TICKER_SLIDE_BUDGET = 60;

  // One set to cover the track, one for the loop to translate away, and the
  // partial set the copy count rounds up to. No loop is seamless below this.
  const MIN_TICKER_COPIES = 3;

  if (customElements.get(TAG)) return;

  class PressCarousel extends HTMLElement {
    #items = [];
    #logos = [];
    #carousel = null;
    #logoList = null;
    #logoListObserver = null;
    #logoRebuildFrame = 0;
    #activeId = null;
    #syncingFromSplide = false;
    #pendingCarouselIndex = null;
    #syncGoIndex = null;
    #autoTimer = null;
    #tempPauseTimer = null;
    #pointerInside = false;
    #focusInside = false;
    #temporaryPaused = false;
    #onCarouselMounted = null;
    #boundSplide = null;
    #pendingFocusLogo = false;
    #focusAttemptId = 0;
    #quoteCollapseTimer = null;
    #tickerMode = false;
    #tickerTrack = null;
    #tickerMetrics = null;
    #tickerObserver = null;
    #tickerResizeTimer = null;
    #motionQuery = null;

    connectedCallback() {
      this.#items = Array.from(this.querySelectorAll('[data-press-item]'));
      this.#carousel = this.querySelector('.splide[data-carousel]');
      this.#logoList = this.querySelector('[data-press-logos]');

      if (this.#items.length === 0) return;

      this.#relocateLogos();
      this.#logos = Array.from(this.querySelectorAll('[data-press-logo]'));
      this.#tickerMode = this.#autoEnabled && !this.#hasQuotes;

      // Logo-only with auto change on: there is no quote to stay in step with,
      // so the row itself is the motion — a continuous scroll rather than the
      // one-logo-at-a-time pager quote mode needs.
      if (this.#tickerMode) {
        this.classList.add('is-ready', 'press__root--ticker', 'press__root--no-quote');
        this.#startTicker();
        return;
      }

      this.#tuneCarouselForLogos();
      this.#mountQuotePager();
      this.#bindEvents();
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);

      const initial =
        this.#items.find((item) => item.classList.contains('is-active')) || this.#items[0];

      this.classList.add('is-ready');

      // The row is `display: none` until `is-ready`, and WebKit cannot decode an
      // image in a hidden subtree — so the repaint has to run here too.
      this.#repaintLogoImages();
      this.#bindLogoImageLoads();
      this.#activate(initial.getAttribute('data-block-id'), { focusLogo: false });

      if (this.#items.length > 1) {
        this.#mountCarousel();
        this.#observeSplide();
        this.#startAutoChange();
      }
    }

    disconnectedCallback() {
      this.#stopAutoChange();
      this.#stopTicker();
      this.#pendingFocusLogo = false;
      this.#focusAttemptId += 1;
      window.clearTimeout(this.#quoteCollapseTimer);
      this.#quoteCollapseTimer = null;
      this.#logoListObserver?.disconnect();
      this.#logoListObserver = null;
      cancelAnimationFrame(this.#logoRebuildFrame);
      this.#logoRebuildFrame = 0;
      this.#returnLogos();

      this.removeEventListener('click', this.#handleLogoClick);
      this.removeEventListener('keydown', this.#handleKeydown);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      this.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.removeEventListener('focusin', this.#handleFocusIn);
      this.removeEventListener('focusout', this.#handleFocusOut);

      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);

      if (this.#carousel) {
        if (this.#onCarouselMounted) {
          this.#carousel.removeEventListener('theme:carousel:mounted', this.#onCarouselMounted);
        }

        this.#carousel.removeEventListener(
          'theme:carousel:destroyed',
          this.#handleCarouselDestroyed
        );

        // Splide's EventInterface wraps listeners, so off(event, fn) never
        // matches and is a no-op; off(event) alone would also drop carousel.js
        // progress/dots handlers. Destroy clears the bus — just drop our ref.
        this.#boundSplide = null;
      }

      this.#onCarouselMounted = null;
    }

    get #autoEnabled() {
      return this.dataset.autoChange === 'true' && this.#items.length > 1;
    }

    get #hasQuotes() {
      return this.#items.some((item) => item.hasAttribute('data-press-has-quote'));
    }

    get #autoIntervalMs() {
      const seconds = Number(this.dataset.autoChangeInterval || 5);

      return Math.max(1, seconds) * 1000;
    }

    get #pauseOnInteraction() {
      return this.dataset.pauseOnInteraction !== 'false';
    }

    get #isPaused() {
      return this.#pointerInside || this.#focusInside || this.#temporaryPaused;
    }

    get #isLoop() {
      return this.#carousel?.getAttribute('data-type') === 'loop';
    }

    // The section counts blocks, not logos: a quote-only block still counts, so
    // the row it asks for can be a loop with one mark in it — which Splide would
    // fill by repeating that one publication across the track. Correct the type
    // against the logos that actually rendered. Runs on element upgrade, ahead
    // of carousel.js's DOMContentLoaded pass, so this is the type it mounts with.
    #tuneCarouselForLogos() {
      if (!this.#carousel || this.#logos.length > 1) return;

      if (this.#logos.length === 0) {
        this.#carousel.removeAttribute('data-carousel');
        return;
      }

      this.#carousel.setAttribute('data-type', 'slide');
    }

    // carousel.js evaluates every carousel once, the first time any section
    // loads it. When a carousel section sits above this one, that pass runs
    // before this element upgrades, while the row is still `display: none`, so
    // it is skipped as unmeasurable — and this section's own copy of the script
    // exits early because the driver already exists. Without this the row stays
    // a static strip until a window resize. Before the driver loads, its own
    // first pass still mounts the row.
    #mountCarousel() {
      if (!this.#carousel?.hasAttribute('data-carousel') || this.#carousel.__splide) return;

      window.themeCarousel?.evaluate(this.#carousel);
    }

    #itemIndex(blockId) {
      return this.#items.findIndex((item) => item.getAttribute('data-block-id') === blockId);
    }

    // Logo button when the quote has one, otherwise the quote-pager dot, otherwise
    // the panel. A quote with no logo still has to take focus when keyboard
    // navigation lands on it.
    #focusTargetFor(blockId) {
      const logo = this.#logos.find((entry) => entry.getAttribute('data-block-id') === blockId);

      if (logo) return logo;

      for (const dot of this.querySelectorAll('[data-press-dot]')) {
        if (dot.getAttribute('data-block-id') === blockId) return dot;
      }

      const item = this.#items.find((entry) => entry.getAttribute('data-block-id') === blockId);
      const panel = item?.querySelector('[data-press-panel]');

      if (!panel) return null;

      panel.tabIndex = -1;
      return panel;
    }

    // Dots stand in for any quote that has no logo, including when other blocks
    // still have logos. One per item, in block order. The shared carousel must
    // not replace them with one dot per logo.
    #mountQuotePager() {
      if (this.#items.length < 2) return;

      const missingLogo = this.#items.some((item) => {
        const blockId = item.getAttribute('data-block-id');

        return !this.#logos.some((logo) => logo.getAttribute('data-block-id') === blockId);
      });

      if (!missingLogo) return;

      const slot = this.querySelector('[data-carousel-dots-slot]');

      if (!slot) return;

      const labelTemplate = this.#carousel?.getAttribute('data-dot-label') || '';
      const list = document.createElement('ul');

      list.className = 'carousel__dots';

      for (const [index, item] of this.#items.entries()) {
        const itemEl = document.createElement('li');
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'carousel__dot';
        dot.dataset.pressDot = '';
        dot.dataset.blockId = item.getAttribute('data-block-id');

        if (labelTemplate) {
          dot.setAttribute('aria-label', labelTemplate.replace('%INDEX%', String(index + 1)));
        }

        itemEl.appendChild(dot);
        list.appendChild(itemEl);
      }

      slot.removeAttribute('data-carousel-dots-slot');
      slot.textContent = '';
      slot.appendChild(list);
      this.classList.add('press__root--quote-pager');

      // Without a remote slot, carousel.js falls back to Splide's own dots:
      // a second row, one per logo, that skips quote-only blocks.
      this.#carousel?.setAttribute('data-pagination', 'none');
    }

    #syncQuoteDots() {
      for (const dot of this.querySelectorAll('[data-press-dot]')) {
        const isActive = dot.getAttribute('data-block-id') === this.#activeId;

        dot.classList.toggle('is-active', isActive);

        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      }
    }

    #relocateLogos() {
      if (!this.#logoList) return;

      for (const item of this.#items) {
        const logo = item.querySelector(':scope > [data-press-logo], [data-press-logo]');

        if (!logo || logo.closest('[data-press-logos]')) continue;

        const slide = document.createElement('div');
        slide.className = 'carousel__slide press__logo-slide';
        slide.appendChild(logo);
        this.#logoList.appendChild(slide);
      }

      // Safari often blanks <img> after a DOM move until decode/repaint runs.
      this.#repaintLogoImages();
    }

    // WebKit can drop the painted bitmap when nodes move (relocate + Splide
    // reparent). Decode and a layout read bring the first logo back without a
    // visible reload flash in Chrome/Firefox. Decoding is skipped while the row
    // is still hidden, where WebKit does no work and the layout read is a no-op.
    #repaintLogoImages() {
      const images = this.#logoList?.querySelectorAll('.press__logo-image');

      if (!images?.length) return;

      for (const image of images) {
        if (typeof image.decode === 'function' && image.getClientRects().length > 0) {
          image.decode().catch(() => {});
        }

        void image.offsetWidth;
      }
    }

    // A fade slide can settle active and opaque with the logo still unpainted in
    // WebKit — the composited layer is never invalidated, so nothing is drawn
    // until the shopper pages. A one-frame no-op transform forces that repaint.
    #nudgeActiveLogoPaint() {
      const active = this.#logos.find(
        (logo) => logo.getAttribute('data-block-id') === this.#activeId
      );
      const image = active?.querySelector('.press__logo-image');

      if (!image || image.getClientRects().length === 0) return;

      image.style.transform = 'translateZ(0)';

      requestAnimationFrame(() => {
        image.style.removeProperty('transform');
      });
    }

    // Logo widths (and the fade stamp) are only trustworthy once the images are
    // decoded, and an eager image can still land after mount on a slow connection.
    #bindLogoImageLoads() {
      for (const logo of this.#logos) {
        const image = logo.querySelector('.press__logo-image');

        if (!image || image.complete || image.dataset.pressPaintBound === 'true') continue;

        image.dataset.pressPaintBound = 'true';
        image.addEventListener('load', this.#handleLogoImageLoad, { once: true });
      }
    }

    #handleLogoImageLoad = () => {
      // Debounced in ticker mode: a set of logos settling one by one must not
      // rebuild (and restart) the loop once per image.
      if (this.#tickerMode) {
        this.#handleTickerResize();
        return;
      }

      // A late logo changes the widths the centred row was laid out from.
      this.#recentreCarousel();
      this.#nudgeActiveLogoPaint();
    };

    #returnLogos() {
      if (!this.#logoList) return;

      for (const slide of this.#slides) {
        const logo = slide.querySelector('[data-press-logo]');
        const blockId = logo?.getAttribute('data-block-id');
        const owner = this.#items.find((item) => item.getAttribute('data-block-id') === blockId);

        if (logo && owner) {
          owner.prepend(logo);
        }

        slide.remove();
      }
    }

    // Delegated: logos are moved into the carousel track after render, and
    // Splide re-parents slides when it mounts.
    #bindEvents() {
      this.addEventListener('click', this.#handleLogoClick);
      this.addEventListener('keydown', this.#handleKeydown);
    }

    // Ticker and Splide clones are both duplicated markup for a loop, never
    // real slides — a block owns exactly one.
    get #slides() {
      if (!this.#logoList) return [];

      return Array.from(
        this.#logoList.querySelectorAll(
          ':scope > .press__logo-slide:not([data-press-clone]):not(.splide__slide--clone)'
        )
      );
    }

    // Every rendered copy of every logo, clones included. Indexing still runs
    // off `#logos` (the real controls, in block order); this is for the state a
    // clone has to mirror to avoid showing a stale highlight.
    #allLogos() {
      if (!this.#logoList) return this.#logos;

      return Array.from(this.#logoList.querySelectorAll('[data-press-logo]'));
    }

    // Clones are recut whenever Splide rebuilds the track — carousel.js calls
    // refresh() on image load and on resize — and each rebuild restores the
    // duplicate buttons this component demotes. Watching the track for that is
    // the only reliable trigger: `refresh` fires *before* Splide's own clone
    // component has run, and there is no matching "done" event to wait for.
    //
    // Direct children only, so demoting a button inside a clone cannot feed the
    // observer its own work back.
    #observeLogoList() {
      if (this.#logoListObserver || !this.#logoList) return;
      if (typeof MutationObserver !== 'function') return;

      this.#logoListObserver = new MutationObserver(() => {
        if (this.#logoRebuildFrame) return;

        // A rebuild lands as several mutations; coalesce them into one pass
        // after the track has settled.
        this.#logoRebuildFrame = requestAnimationFrame(() => {
          this.#logoRebuildFrame = 0;
          this.#syncLogoClones();
          this.#recentreCarousel();
        });
      });

      this.#logoListObserver.observe(this.#logoList, { childList: true });
    }

    // A loop clone is duplicated markup: same ids, same pressed state, frozen at
    // the moment Splide cut it. Demote each to a plain span — as the ticker does
    // with its own clones — so one publication never exposes two controls, or
    // two copies of an id a quote panel is labelled by. Clicks still land: the
    // row's delegated handler reads the block id, so tapping an edge copy
    // selects that publication.
    //
    // Splide re-clones on refresh (image loads, resize), so this runs again on
    // every rebuild rather than once at mount.
    #syncLogoClones() {
      if (!this.#logoList) return;

      const clones = this.#logoList.querySelectorAll(':scope > .splide__slide--clone');

      for (const clone of clones) {
        for (const logo of clone.querySelectorAll('[data-press-logo]')) {
          if (logo.tagName !== 'BUTTON') continue;

          const span = document.createElement('span');

          span.className = 'press__logo';
          span.dataset.pressLogo = '';
          span.dataset.blockId = logo.getAttribute('data-block-id');
          span.setAttribute('aria-hidden', 'true');

          while (logo.firstChild) span.appendChild(logo.firstChild);

          logo.replaceWith(span);
        }
      }

      this.#syncLogoState();
    }

    // Splide fixes the row position at mount and again on every refresh, and
    // either can land on a stale measurement — a logo that decodes late leaves
    // the track translated for widths that no longer apply, which puts the
    // pressed logo off the centre line with nothing to correct it: go() is a
    // no-op at the index it is already on. Re-assert the position instead.
    #recentreCarousel() {
      const splide = this.#carousel?.__splide;
      const move = splide?.Components?.Move;

      if (typeof move?.jump !== 'function') return;

      // 4 is Splide's MOVING state; jumping mid-animation would cut it short.
      if (splide.state?.is?.(4)) return;

      const target = move.toPosition(splide.index, true);

      // Sub-pixel drift is invisible, and repositioning on it would thrash.
      if (Math.abs(move.getPosition() - target) < 1) return;

      move.jump(splide.index);
    }

    // Applied to every copy by block id: a clone cut before the current
    // selection would otherwise keep whichever highlight it was cloned with.
    // `aria-pressed` stays on the real controls — the clones are not controls.
    #syncLogoState() {
      for (const logo of this.#allLogos()) {
        const isActive = logo.getAttribute('data-block-id') === this.#activeId;

        logo.classList.toggle('is-active', isActive);

        if (logo.tagName === 'BUTTON') {
          logo.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        }
      }
    }

    #observeSplide() {
      if (!this.#carousel) return;

      this.#onCarouselMounted = () => {
        const splide = this.#carousel.__splide;

        if (!splide) return;

        // Bind once per Splide instance. Remounts create a new instance; the
        // previous bus is destroyed with it, so we must not call off() here.
        if (this.#boundSplide !== splide) {
          this.#boundSplide = splide;
          splide.on('moved', this.#handleSplideMoved);
          splide.on('drag', this.#handleSplideDrag);

          // Re-measuring the row moves the centre line the active logo sits on.
          splide.on('updated resized', this.#handleSplideRestamp);
        }

        this.#observeLogoList();
        this.#syncCarouselToActive();
        this.#recentreCarousel();
        this.#syncLogoClones();
        this.#repaintLogoImages();
        this.#nudgeActiveLogoPaint();

        // Finish deferred focus after mount/sync settles.
        if (this.#pendingFocusLogo && !this.#syncingFromSplide) {
          this.#focusActiveLogo();
        }
      };

      this.#carousel.addEventListener('theme:carousel:mounted', this.#onCarouselMounted);
      this.#carousel.addEventListener('theme:carousel:destroyed', this.#handleCarouselDestroyed);
      this.#onCarouselMounted();
    }

    #clearSyncGate() {
      this.#syncingFromSplide = false;
      this.#pendingCarouselIndex = null;
      this.#syncGoIndex = null;
    }

    #handleCarouselDestroyed = () => {
      this.#boundSplide = null;
      this.#clearSyncGate();

      // Finish deferred focus now that the static row owns the logos.
      if (this.#pendingFocusLogo) {
        this.#focusActiveLogo();
      }
    };


    #handleLogoClick = (event) => {
      if (this.#items.length < 2) return;

      const dot = event.target.closest('[data-press-dot]');

      if (dot && this.contains(dot)) {
        this.#pauseAutoTemporarily();
        this.#activate(dot.getAttribute('data-block-id'), {
          focusLogo: false,
          syncCarousel: true,
        });
        return;
      }

      const logo = event.target.closest('[data-press-logo]');

      if (!logo) return;

      this.#pauseAutoTemporarily();
      this.#activate(logo.getAttribute('data-block-id'), {
        focusLogo: false,
        syncCarousel: true,
      });
    };

    #handleKeydown = (event) => {
      if (this.#items.length < 2) return;

      const onControl = event.target.closest('[data-press-logo], [data-press-dot]');
      const onPanel = event.target.matches('[data-press-panel]');

      if (!onPanel && !(onControl && this.contains(onControl))) return;

      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

      if (!keys.includes(event.key)) return;

      event.preventDefault();

      const currentIndex = this.#itemIndex(this.#activeId);
      let nextIndex = currentIndex;

      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.#items.length - 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = currentIndex <= 0 ? this.#items.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= this.#items.length - 1 ? 0 : currentIndex + 1;
      }

      const next = this.#items[nextIndex];

      if (!next) return;

      this.#pauseAutoTemporarily();
      this.#activate(next.getAttribute('data-block-id'), {
        focusLogo: true,
        syncCarousel: true,
      });
    };

    // A refresh rebuilds the clones, so they need demoting and restamping again,
    // and re-measures the row the centring is derived from.
    #handleSplideRestamp = () => {
      this.#syncLogoClones();
      this.#recentreCarousel();
      this.#nudgeActiveLogoPaint();
    };

    #handleSplideDrag = () => {
      // A shopper drag cancels an in-flight programmatic sync so the ensuing
      // `moved` event is treated as user navigation (not a stale go() race).
      this.#clearSyncGate();
    };

    #handleSplideMoved = (index) => {
      if (this.#syncingFromSplide) {
        if (index === this.#syncGoIndex) {
          this.#clearSyncGate();
          // Logo buttons on inactive fade slides are not focusable until Splide
          // finishes, so deferred focus from #activate lands here.
          if (this.#pendingFocusLogo) this.#focusActiveLogo();
          return;
        }

        // Rapid clicks supersede an in-flight go(); ignore intermediate landings
        // while Splide is still moving toward the latest target.
        const splide = this.#carousel?.__splide;
        const stillMoving = splide?.state?.is?.(4);

        if (stillMoving) return;

        // Settled on an unexpected index after a superseded go — ignore it so
        // a late moved event cannot overwrite the shopper's newer selection,
        // then bring the pager back to the active logo (no-op if already there)
        // so the mobile fade slide can't stay out of step with the quote.
        this.#clearSyncGate();
        this.#syncCarouselToActive();
        return;
      }

      const logo = this.#logos[index];

      if (!logo || this.classList.contains('press__root--quote-only-active')) return;

      this.#pauseAutoTemporarily();
      this.#activate(logo.getAttribute('data-block-id'), {
        focusLogo: false,
        syncCarousel: false,
      });
    };

    #handleBlockSelect = (event) => {
      if (!event.target || !this.contains(event.target)) return;

      const item = event.target.closest('[data-press-item]');
      const logo = event.target.closest('[data-press-logo]');
      const blockId =
        item?.getAttribute('data-block-id') ||
        logo?.getAttribute('data-block-id') ||
        event.detail?.blockId ||
        event.target.getAttribute?.('data-block-id');

      if (!blockId) return;

      this.#activate(blockId, { focusLogo: false, syncCarousel: true });
    };

    #activate(blockId, { focusLogo = false, syncCarousel = false } = {}) {
      if (!blockId) return;

      const previousId = this.#activeId;

      this.#activeId = blockId;

      // Capture before applying inert — browsers may blur unpredictably once
      // the focused node sits under an inert ancestor.
      const priorFocus = document.activeElement;
      const priorPanel = priorFocus?.closest?.('[data-press-panel]');
      const priorLogo = priorFocus?.closest?.('[data-press-logo]');
      const focusLeavingQuote =
        !!priorPanel &&
        priorPanel.closest('[data-press-item]')?.getAttribute('data-block-id') !== blockId;
      const focusOnStaleLogo =
        !!priorLogo && priorLogo.getAttribute('data-block-id') !== blockId;

      for (const item of this.#items) {
        const isActive = item.getAttribute('data-block-id') === blockId;
        const panel = item.querySelector('[data-press-panel]');

        item.classList.toggle('is-active', isActive);

        if (panel) {
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          panel.inert = !isActive;
        }
      }

      this.#syncLogoState();
      this.#syncQuoteDots();
      this.#syncQuoteArea(previousId, blockId);

      // A quote with no logo has no slide. Hide the row while it is showing so
      // the previous publication is not left centred under a different quote.
      const activeHasLogo = this.#logos.some(
        (logo) => logo.getAttribute('data-block-id') === blockId
      );

      this.classList.toggle('press__root--quote-only-active', this.#logos.length > 0 && !activeHasLogo);

      const shouldFocus = focusLogo || focusLeavingQuote || focusOnStaleLogo;

      this.#pendingFocusLogo = shouldFocus;

      if (syncCarousel) this.#syncCarouselToActive();

      // If Splide is mid-go the target logo may not be focusable yet — wait for
      // #handleSplideMoved. Otherwise focus immediately.
      if (shouldFocus && !this.#syncingFromSplide) {
        this.#focusActiveLogo();
      }
    }

    #panelTransitionMs() {
      const raw = getComputedStyle(this).getPropertyValue('--press-transition-duration').trim();
      const value = Number.parseFloat(raw);

      if (!Number.isFinite(value)) return 300;

      return raw.endsWith('ms') ? value : value * 1000;
    }

    #syncQuoteArea(previousId, blockId) {
      const next = this.#items.find((item) => item.getAttribute('data-block-id') === blockId);
      const previous = this.#items.find((item) => item.getAttribute('data-block-id') === previousId);
      const nextHasQuote = Boolean(next?.querySelector('[data-press-panel]'));
      const previousHadQuote = Boolean(previous?.querySelector('[data-press-panel]'));

      if (nextHasQuote) {
        window.clearTimeout(this.#quoteCollapseTimer);
        this.#quoteCollapseTimer = null;
        this.classList.remove('press__root--no-quote');
        return;
      }

      // Quote → logo-only: wait for the dissolve so the fade is not cut off.
      if (previousHadQuote) {
        window.clearTimeout(this.#quoteCollapseTimer);
        this.#quoteCollapseTimer = window.setTimeout(() => {
          this.#quoteCollapseTimer = null;

          // Collapse if the current item is still logo-only — a later hop to
          // another logo-only item must not skip this (the original blockId
          // may no longer be active).
          const active = this.#items.find(
            (item) => item.getAttribute('data-block-id') === this.#activeId
          );

          if (active?.querySelector('[data-press-panel]')) return;

          this.classList.add('press__root--no-quote');
        }, this.#panelTransitionMs());
        return;
      }

      // Logo-only → logo-only (or re-activate) during an in-flight dissolve:
      // keep the pending collapse so --no-quote is not added early.
      if (this.#quoteCollapseTimer) return;

      // Initial paint / logo-only → logo-only with no pending fade: collapse now.
      this.classList.add('press__root--no-quote');
    }

    #focusActiveLogo() {
      const targetId = this.#activeId;
      const activeLogo = this.#focusTargetFor(targetId);

      if (!activeLogo) {
        this.#pendingFocusLogo = false;
        return;
      }

      const attemptId = ++this.#focusAttemptId;

      const attempt = (triesLeft) => {
        requestAnimationFrame(() => {
          // A newer activate/focus superseded this chain.
          if (attemptId !== this.#focusAttemptId || this.#activeId !== targetId) return;

          activeLogo.focus({ preventScroll: true });

          if (document.activeElement === activeLogo) {
            this.#pendingFocusLogo = false;
            return;
          }

          // Fade slides can reject focus for a frame or two after Splide
          // settles — retry briefly, then drop the pending flag.
          if (triesLeft > 0) {
            attempt(triesLeft - 1);
            return;
          }

          if (attemptId === this.#focusAttemptId) {
            this.#pendingFocusLogo = false;
          }
        });
      };

      attempt(5);
    }

    #syncCarouselToActive() {
      const splide = this.#carousel?.__splide;

      if (!splide) return;

      const index = this.#logos.findIndex(
        (logo) => logo.getAttribute('data-block-id') === this.#activeId
      );

      if (index < 0) {
        this.#clearSyncGate();
        return;
      }

      const maxIndex = splide.Components?.Controller?.getEnd?.() ?? index;
      const target = Math.min(index, maxIndex);

      if (target === splide.index) {
        this.#clearSyncGate();
        return;
      }

      this.#pendingCarouselIndex = target;
      this.#syncGoIndex = target;
      this.#syncingFromSplide = true;
      splide.go(this.#loopStep(splide, target) || target);
    }

    // An absolute go() walks the row to the slide's real position, so reaching
    // the first logo from the last rewinds the whole row rather than wrapping
    // (1371px against 207px for one slot, measured on eight logos). Ask for the
    // shorter way round as a relative move instead: Splide resolves those
    // through the clones, which is what makes the seam read as a rotation.
    // It lands on the index we asked for either way, so the sync gate is
    // unaffected.
    #loopStep(splide, target) {
      if (!this.#isLoop) return null;

      const count = this.#logos.length;
      const forward = (target - splide.index + count) % count;

      if (count < 2 || forward === 0) return null;

      const backward = count - forward;

      // Splide only clones enough slides to cover the track, so a relative move
      // further than that would translate past the last copy and pull empty
      // track into view. Longer jumps stay absolute — they are a long move
      // whichever direction they take.
      const clonesPerSide = splide.root.querySelectorAll('.splide__slide--clone').length / 2;

      if (Math.min(forward, backward) > clonesPerSide) return null;

      return forward <= backward ? `+${forward}` : `-${backward}`;
    }

    #startTicker() {
      if (!this.#logoList) return;

      this.#tickerTrack = this.#logoList.closest('.carousel__track');

      // The shared carousel is quote mode's mobile pager: one logo per view,
      // cross-fading with the quote. A ticker owns the track instead, so opt the
      // row out before carousel.js evaluates it (this runs on element upgrade,
      // ahead of its DOMContentLoaded pass).
      if (this.#carousel) {
        if (this.#carousel.__splide) window.themeCarousel?.destroyAll(this);

        this.#carousel.removeAttribute('data-carousel');
      }

      this.#demoteLogosToStatic();

      this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#motionQuery.addEventListener('change', this.#handleTickerResize);

      if (this.#pauseOnInteraction) {
        this.addEventListener('pointerenter', this.#handleTickerPause);
        this.addEventListener('pointerleave', this.#handleTickerResume);
        this.addEventListener('focusin', this.#handleTickerPause);
        this.addEventListener('focusout', this.#handleTickerFocusOut);
      }

      if (typeof ResizeObserver === 'function' && this.#tickerTrack) {
        this.#tickerObserver = new ResizeObserver(this.#handleTickerResize);
        this.#tickerObserver.observe(this.#tickerTrack);
      }

      window.addEventListener('resize', this.#handleTickerResize);

      this.#bindLogoImageLoads();
      this.#syncTicker();

      // Demoting the buttons moves every image into a new span, so the decode
      // has to come after the row is built — not before, or WebKit can keep the
      // dropped bitmap until something else invalidates the layer.
      this.#repaintLogoImages();
    }

    // With no quote to reveal there is nothing for a logo to activate, so the
    // buttons become plain elements: a row of controls that do nothing is worse
    // for keyboard and screen reader users than the images on their own. The id
    // is kept so a design-mode placeholder panel's `aria-labelledby` still resolves.
    #demoteLogosToStatic() {
      const demoted = [];

      for (const logo of this.#logos) {
        const span = document.createElement('span');

        span.className = 'press__logo';
        span.id = logo.id;
        span.dataset.pressLogo = '';
        span.dataset.blockId = logo.getAttribute('data-block-id');

        while (logo.firstChild) span.appendChild(logo.firstChild);

        logo.replaceWith(span);
        demoted.push(span);
      }

      this.#logos = demoted;
    }

    #stopTicker() {
      if (!this.#tickerMode) return;

      window.clearTimeout(this.#tickerResizeTimer);
      this.#tickerResizeTimer = null;

      this.#tickerObserver?.disconnect();
      this.#tickerObserver = null;

      this.#motionQuery?.removeEventListener('change', this.#handleTickerResize);
      this.#motionQuery = null;

      window.removeEventListener('resize', this.#handleTickerResize);

      this.removeEventListener('pointerenter', this.#handleTickerPause);
      this.removeEventListener('pointerleave', this.#handleTickerResume);
      this.removeEventListener('focusin', this.#handleTickerPause);
      this.removeEventListener('focusout', this.#handleTickerFocusOut);

      this.#clearTickerClones();
      this.#tickerMetrics = null;
      this.#tickerTrack = null;
    }

    // The loop translates the row by exactly one set of logos, so enough copies
    // have to sit beyond the visible track for the tail never to expose it.
    #syncTicker() {
      const list = this.#logoList;
      const track = this.#tickerTrack;

      if (!list || !track) return;

      // Reduced motion keeps every logo readable and reachable without moving —
      // a wrapped row rather than a clipped strip that can no longer scroll.
      if (this.#motionQuery?.matches) {
        this.#wrapTickerRow(list);
        return;
      }

      const slides = this.#slides;

      if (slides.length === 0) return;

      const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
      let setWidth = 0;

      for (const [index, slide] of slides.entries()) {
        if (index > 0) setWidth += gap;

        setWidth += slide.getBoundingClientRect().width;
      }

      // One set plus its trailing gap: the distance at which the next copy sits
      // exactly where this one started.
      const advance = setWidth + gap;
      const viewport = track.clientWidth;

      if (advance <= 0 || viewport <= 0) return;

      // One set to cover the track, one for the loop to translate away, plus the
      // partial set the ceiling rounds off. Fewer than this and the tail leaves
      // a gap of empty track that jumps when the animation wraps.
      const copies = Math.ceil(viewport / advance) + 2;

      // A set wider than the track already needs only the minimum, so the budget
      // must never bite there: it exists to stop a narrow set cloning without
      // end, not to punish a long one for the slides the merchant authored.
      const affordable = Math.max(
        MIN_TICKER_COPIES,
        Math.floor(TICKER_SLIDE_BUDGET / slides.length)
      );

      // Only a set too narrow to reach across the track within that budget lands
      // here. Wrapping shows every logo intact, which a seam in the loop would not.
      if (copies > affordable) {
        this.#wrapTickerRow(list);
        return;
      }

      // Left until the row is known to be animating: toggling it off to measure
      // and back on to wrap would be layout work for a state that never renders.
      this.classList.remove('press__root--ticker-static');

      // Rebuilding restarts the animation, so leave a row that already fits
      // alone — resize noise (mobile browser chrome) must not jump the logos.
      // Sub-pixel width drift is not a change worth reacting to.
      const settled =
        this.#tickerMetrics?.copies === copies &&
        Math.abs(this.#tickerMetrics.advance - advance) <= 0.5;

      if (settled) return;

      this.#clearTickerClones();

      for (let copy = 1; copy < copies; copy += 1) {
        for (const slide of slides) list.appendChild(this.#tickerClone(slide));
      }

      // One logo passes per auto-change interval, so the setting a merchant
      // already understands keeps driving the pace.
      const seconds = (this.#autoIntervalMs / 1000) * slides.length;

      list.style.setProperty('--press-ticker-distance', `${advance}px`);
      list.style.setProperty('--press-ticker-duration', `${seconds}s`);
      this.#tickerMetrics = { advance, copies };

      // Fresh clones carry unpainted images, and a rebuild restarts the loop —
      // decode now rather than leaving a blank gap to scroll past.
      this.#repaintLogoImages();
    }

    // The row without the loop: real logos only, wrapped and centred rather than
    // clipped inside a track that is no longer scrolling.
    #wrapTickerRow(list) {
      this.#clearTickerClones();
      this.#tickerMetrics = null;
      this.classList.add('press__root--ticker-static');
      list.style.removeProperty('--press-ticker-distance');
      list.style.removeProperty('--press-ticker-duration');
    }

    #tickerClone(slide) {
      const clone = slide.cloneNode(true);

      clone.dataset.pressClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;

      // Duplicated markup must not answer to this component's queries or repeat
      // the outlet names to a screen reader — ids and block ids are the original's.
      for (const node of clone.querySelectorAll('[id]')) {
        node.removeAttribute('id');
      }

      for (const node of clone.querySelectorAll('[data-press-logo]')) {
        node.removeAttribute('data-press-logo');
        node.removeAttribute('data-block-id');
      }

      return clone;
    }

    #clearTickerClones() {
      if (!this.#logoList) return;

      for (const clone of this.#logoList.querySelectorAll(':scope > [data-press-clone]')) {
        clone.remove();
      }
    }

    #handleTickerResize = () => {
      window.clearTimeout(this.#tickerResizeTimer);

      this.#tickerResizeTimer = window.setTimeout(() => {
        this.#tickerResizeTimer = null;
        this.#syncTicker();
      }, 120);
    };

    #handleTickerPause = () => {
      this.#logoList?.classList.add('is-paused');
    };

    #handleTickerResume = () => {
      this.#logoList?.classList.remove('is-paused');
    };

    #handleTickerFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;

      this.#handleTickerResume();
    };

    #startAutoChange() {
      this.#stopAutoChange();

      if (!this.#autoEnabled) return;

      if (this.#pauseOnInteraction) {
        this.addEventListener('pointerenter', this.#handlePointerEnter);
        this.addEventListener('pointerleave', this.#handlePointerLeave);
        this.addEventListener('focusin', this.#handleFocusIn);
        this.addEventListener('focusout', this.#handleFocusOut);
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.#autoTimer = window.setInterval(() => {
        if (this.#isPaused) return;

        const currentIndex = this.#itemIndex(this.#activeId);
        const nextIndex =
          currentIndex < 0 || currentIndex >= this.#items.length - 1 ? 0 : currentIndex + 1;
        const next = this.#items[nextIndex];

        if (!next) return;

        this.#activate(next.getAttribute('data-block-id'), {
          focusLogo: false,
          syncCarousel: true,
        });
      }, this.#autoIntervalMs);
    }

    #stopAutoChange() {
      if (this.#autoTimer) {
        clearInterval(this.#autoTimer);
        this.#autoTimer = null;
      }

      if (this.#tempPauseTimer) {
        clearTimeout(this.#tempPauseTimer);
        this.#tempPauseTimer = null;
      }
    }

    #handlePointerEnter = () => {
      this.#pointerInside = true;
    };

    #handlePointerLeave = () => {
      this.#pointerInside = false;
    };

    #handleFocusIn = () => {
      this.#focusInside = true;
    };

    #handleFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.#focusInside = false;
    };

    #pauseAutoTemporarily() {
      if (!this.#pauseOnInteraction || !this.#autoEnabled) return;

      this.#temporaryPaused = true;

      if (this.#tempPauseTimer) clearTimeout(this.#tempPauseTimer);

      this.#tempPauseTimer = window.setTimeout(() => {
        this.#temporaryPaused = false;
        this.#tempPauseTimer = null;
      }, this.#autoIntervalMs);
    }
  }

  customElements.define(TAG, PressCarousel);
})();
